import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { INK, OLIVE, SERIF, BODY, Column } from './DagbladFrontPage';
import { useRewards, KidRewards, RewardsAdmin } from './KidRewards';
import KidChores from './KidChores';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

function KidCard({ kid: initialKid, edition, onSticker, rewards }) {
  const [busy, setBusy] = useState(false);
  const [kid, setKid] = useState(initialKid);
  useEffect(() => setKid(initialKid), [initialKid]);
  const gotChoreSticker = useCallback((st) => {
    setKid((k) => {
      if (!st.removed) return { ...k, stickers_total: k.stickers_total + 1, album: [st.emoji, ...k.album].slice(0, 12) };
      const i = k.album.indexOf(st.emoji);
      return { ...k, stickers_total: k.stickers_total - 1, album: i < 0 ? k.album : k.album.filter((_, j) => j !== i) };
    });
    window.dispatchEvent(new Event('focus-critter:rewards-changed'));
  }, []);
  const total = kid.items.length;
  const ready = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/dagblad/kid-ready`, { kid_id: kid.id, edition }, cfg);
      onSticker(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte dela ut klistermärket'); }
    setBusy(false);
  };
  return (
    <div className="border-2 border-dashed p-3 relative" style={{ borderColor: OLIVE, background: '#FFFBF2' }} data-testid={`kidcol-${kid.id}`}>
      <h4 className="text-xl font-black leading-tight" style={{ fontFamily: SERIF, color: INK }}>{kid.name}s hörna</h4>
      <span className="text-[11px] font-black" style={{ color: OLIVE }} data-testid={`kidcol-${kid.id}-total`}>⭐ {kid.stickers_total} {kid.stickers_total === 1 ? 'klistermärke' : 'klistermärken'} samlat totalt</span>
      <p className="text-sm mt-1" style={{ fontFamily: BODY }} data-testid={`kidcol-${kid.id}-day`}>
        {kid.activities.length ? `${edition === 'evening' ? 'Imorgon' : 'Idag'}: ${kid.activities.join(', ')} 🎽` : `${edition === 'evening' ? 'Imorgon' : 'Idag'}: en fri dag att leka 🌼`}
      </p>
      <div className="mt-2 rounded-2xl px-3 py-2 text-sm italic relative" style={{ background: '#F1E7D0', fontFamily: BODY }} data-testid={`kidcol-${kid.id}-joke`}>
        😄 {kid.joke}
      </div>
      <div className="mt-3 flex items-center gap-3">
        {kid.sticker ? (
          <div className="flex items-center gap-2" data-testid={`kidcol-${kid.id}-sticker`}>
            <span className="text-5xl animate-bounce" style={{ animationIterationCount: 3 }}>{kid.sticker.emoji}</span>
            <span className="text-sm font-bold" style={{ fontFamily: BODY }}>{kid.sticker.reason} Dagens klistermärke!</span>
          </div>
        ) : total > 0 ? (
          <div className="flex items-center gap-2 text-sm" style={{ fontFamily: BODY }} data-testid={`kidcol-${kid.id}-progress`}>
            <span className="text-4xl grayscale opacity-30">{kid.sticker_preview}</span>
            <span>Packa klart väskan ({kid.packed}/{total}) så får {kid.name} dagens klistermärke!</span>
          </div>
        ) : (
          <button onClick={ready} disabled={busy} className="no-print flex items-center gap-2 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-[#F4EEE1] disabled:opacity-50"
            style={{ background: OLIVE }} data-testid={`kidcol-${kid.id}-ready`}>
            <span className="text-lg">{kid.sticker_preview}</span> {kid.name} är redo!
          </button>
        )}
      </div>
      <KidChores kid={kid} onGotSticker={gotChoreSticker} />
      {kid.album.length > 0 && (
        <div className="mt-2 text-lg tracking-wide" title="Senaste klistermärkena" data-testid={`kidcol-${kid.id}-album`}>{kid.album.join(' ')}</div>
      )}
      <KidRewards kid={kid} rewards={rewards} />
    </div>
  );
}

export function KidsColumn({ p, onSticker, onHide }) {
  const rewards = useRewards();
  if (!p.kid_column?.length) return null;
  return (
    <Column title="Barnens spalt" testid="dagblad-kidcol" onHide={onHide}>
      <div className="grid sm:grid-cols-2 gap-3">
        {p.kid_column.map((k) => <KidCard key={k.id} kid={k} edition={p.edition} onSticker={(st) => { onSticker(st); window.dispatchEvent(new Event('focus-critter:rewards-changed')); }} rewards={rewards} />)}
      </div>
      <RewardsAdmin rewards={rewards} />
    </Column>
  );
}

export const applySticker = (p, st) => ({
  ...p,
  kid_column: p.kid_column.map((k) => (k.id === st.kid_id
    ? { ...k, sticker: { emoji: st.emoji, reason: st.reason }, stickers_total: k.stickers_total + 1, album: [st.emoji, ...k.album].slice(0, 12) }
    : k)),
});
